/**
 * BrandAtoms — the per-pane brand atoms footer. Rendered as the sticky
 * bottom strip on /brand, mirroring HeaderBar's cut-off treatment but
 * anchored to the bottom of the screen. Fixed height so switching between
 * tabs doesn't change the page scroll position.
 *
 * Tabs (in order): Colors · Fonts · Spacing · Text
 */
import { useEffect, useState } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { COLORS, RADII, SPACING, STROKES } from "../../lib/brand";
import { COLOR_SLOTS, STYLE_SLOTS, type PaneTheme } from "./paneThemes";

export const FOOTER_HEIGHT = 440;

const FOOTER_BORDER = "rgba(247,244,238,0.10)";
const FOOTER_TEXT = COLORS.LINEN.hex;
const FOOTER_TEXT_DIM = "rgba(247,244,238,0.62)";
const FOOTER_TEXT_FAINT = "rgba(247,244,238,0.36)";
const HOVER_BG = "rgba(247,244,238,0.06)";

const TABS = ["Colors", "Fonts", "Spacing", "Text", "Style"] as const;
type TabKey = (typeof TABS)[number];

export function BrandAtoms({
  theme,
  wide,
  pad,
}: {
  theme: PaneTheme;
  wide: boolean;
  pad: number;
}) {
  const [tab, setTab] = useState<TabKey>("Colors");

  return (
    <View
      style={{
        flex: 1,
        paddingHorizontal: pad,
        paddingTop: 18,
        paddingBottom: 22,
        gap: 16,
      }}
    >
      <View style={{ alignItems: "center" }}>
        <TabSelector value={tab} onChange={setTab} accent={theme.accent} />
      </View>
      <View style={{ flex: 1, overflow: "hidden" }}>
        {tab === "Colors" ? <ColorsTab theme={theme} /> : null}
        {tab === "Fonts" ? <FontsTab theme={theme} wide={wide} /> : null}
        {tab === "Spacing" ? <SpacingTab theme={theme} wide={wide} /> : null}
        {tab === "Text" ? <TextTab theme={theme} wide={wide} /> : null}
        {tab === "Style" ? <StyleTab theme={theme} wide={wide} /> : null}
      </View>
    </View>
  );
}

/* ─────────────────────── tab selector ─────────────────────── */

const SLOT_WIDTH = 96;
const SLOT_HEIGHT = 30;
const SELECTOR_PAD = 3;

function TabSelector({
  value,
  onChange,
  accent,
}: {
  value: TabKey;
  onChange: (next: TabKey) => void;
  accent: string;
}) {
  const activeIndex = TABS.indexOf(value);
  const indicatorX = useSharedValue(activeIndex * SLOT_WIDTH);

  useEffect(() => {
    indicatorX.value = withTiming(activeIndex * SLOT_WIDTH, {
      duration: 280,
      easing: Easing.bezier(0.32, 0.72, 0, 1),
    });
  }, [activeIndex, indicatorX]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
  }));

  return (
    <View
      style={{
        flexDirection: "row",
        borderRadius: 999,
        padding: SELECTOR_PAD,
        borderWidth: 1,
        borderColor: FOOTER_BORDER,
        position: "relative",
        height: 36,
        backgroundColor: "rgba(0,0,0,0.25)",
      }}
    >
      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            top: SELECTOR_PAD,
            left: SELECTOR_PAD,
            width: SLOT_WIDTH,
            height: SLOT_HEIGHT,
            borderRadius: 999,
            backgroundColor: `${accent}26`,
            borderWidth: 1,
            borderColor: `${accent}88`,
          },
          indicatorStyle,
        ]}
      />
      {TABS.map((t) => {
        const active = t === value;
        return (
          <Pressable
            key={t}
            accessibilityRole="button"
            onPress={() => onChange(t)}
            style={({ hovered }) => [
              {
                width: SLOT_WIDTH,
                height: SLOT_HEIGHT,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
              },
              Platform.OS === "web" && hovered && !active
                ? ({ backgroundColor: HOVER_BG } as ViewStyle)
                : null,
            ]}
          >
            <Text
              style={{
                fontFamily: "DMSans_600SemiBold",
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: active ? accent : FOOTER_TEXT_DIM,
              }}
            >
              {t}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ─────────────────────── Colors ─────────────────────── */

function ColorsTab({ theme }: { theme: PaneTheme }) {
  return (
    <View style={{ flexDirection: "row", gap: 10, flex: 1, alignItems: "stretch" }}>
      {COLOR_SLOTS.map((slot) => {
        const tokenName = theme.atoms.colors[slot];
        const token = COLORS[tokenName];
        if (!token) return null;
        return (
          <View
            key={slot}
            style={{
              flex: 1,
              borderRadius: 8,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: FOOTER_BORDER,
              backgroundColor: "rgba(0,0,0,0.25)",
            }}
          >
            <View style={{ flex: 1, backgroundColor: token.hex, minHeight: 80 }} />
            <View style={{ padding: 11, gap: 3 }}>
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 10.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: FOOTER_TEXT,
                }}
                numberOfLines={1}
              >
                {slot}
              </Text>
              <Text
                style={{
                  fontFamily: "DMSans_400Regular",
                  fontSize: 10.5,
                  color: FOOTER_TEXT_DIM,
                }}
                numberOfLines={1}
              >
                {token.hex}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

/* ─────────────────────── Fonts ─────────────────────── */

type FontSlot = {
  title: string;
  font: string;
  size: number;
  lineHeight: number;
  letterSpacing?: number;
  transform?: "uppercase";
  sample: string;
  accent?: boolean;
};

function getFontSlots(theme: PaneTheme): FontSlot[] {
  return [
    {
      title: "Display Headline",
      font: theme.displayMediumFont,
      size: 30,
      lineHeight: 34,
      sample: "Display Headline",
    },
    {
      title: "Italic Accent",
      font: theme.displayItalicFont,
      size: 28,
      lineHeight: 32,
      sample: "italic accent",
      accent: true,
    },
    {
      title: "Eyebrow",
      font: theme.bodySemiBoldFont,
      size: 12,
      lineHeight: 14,
      letterSpacing: 3.2,
      transform: "uppercase",
      sample: "Eyebrow Label",
    },
    {
      title: "Body",
      font: theme.bodyFont,
      size: 14,
      lineHeight: 22,
      sample: "Body copy sample.",
    },
    {
      title: "Button Label",
      font: theme.bodyBoldFont,
      size: 12,
      lineHeight: 16,
      letterSpacing: 1.6,
      transform: "uppercase",
      sample: "Button Label",
    },
  ];
}

function FontsTab({ theme, wide }: { theme: PaneTheme; wide: boolean }) {
  const slots = getFontSlots(theme);
  const cellWidth = wide ? "33.333%" : "50%";
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        marginHorizontal: -6,
        marginVertical: -6,
      }}
    >
      {slots.map((slot) => (
        <View key={slot.title} style={{ width: cellWidth as ViewStyle["width"], padding: 6 }}>
          <View
            style={{
              backgroundColor: "rgba(0,0,0,0.25)",
              borderWidth: 1,
              borderColor: FOOTER_BORDER,
              borderRadius: 8,
              paddingVertical: 12,
              paddingHorizontal: 14,
              gap: 8,
              minHeight: 128,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}
            >
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 10.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: FOOTER_TEXT,
                }}
              >
                {slot.title}
              </Text>
              <Text
                style={{
                  fontFamily: "DMSans_400Regular",
                  fontSize: 9.5,
                  color: FOOTER_TEXT_FAINT,
                }}
              >
                {slot.font.split("_")[0]} · {slot.size}/{slot.lineHeight}
              </Text>
            </View>
            <Text
              numberOfLines={2}
              style={{
                fontFamily: slot.font,
                fontSize: slot.size,
                lineHeight: slot.lineHeight,
                letterSpacing: slot.letterSpacing,
                textTransform: slot.transform,
                color: slot.accent ? theme.accent : FOOTER_TEXT,
              }}
            >
              {slot.sample}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

/* ─────────────────────── Spacing ─────────────────────── */

// Live readout of the brand layout tokens (lib/brand.ts). The display labels
// preserve the original "2xs"/"2xl" notation for the doubled ends of the
// spacing scale; everything else is the token key verbatim. Editing a value
// in SPACING/RADII/STROKES moves these rows AND the shared aspects/shells that
// consume the same tokens.
const SPACING_DISPLAY: Partial<Record<keyof typeof SPACING, string>> = {
  xxs: "2xs",
  xxl: "2xl",
};
const SPACING_SCALE = (Object.entries(SPACING) as [keyof typeof SPACING, number][]).map(
  ([name, size]) => ({ name: SPACING_DISPLAY[name] ?? name, size }),
);

const RADII_SCALE = (Object.entries(RADII) as [keyof typeof RADII, number][]).map(
  ([name, size]) => ({ name, size }),
);

const STROKES_SCALE = Object.values(STROKES);

function SpacingTab({ theme, wide }: { theme: PaneTheme; wide: boolean }) {
  return (
    <View style={{ flexDirection: wide ? "row" : "column", gap: 18, flex: 1 }}>
      <SpacingColumn title="Spacing scale" accent={theme.accent}>
        <View style={{ gap: 8 }}>
          {SPACING_SCALE.map((s) => (
            <View key={s.name} style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <Text
                style={{
                  width: 36,
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 10.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOOTER_TEXT_DIM,
                }}
              >
                {s.name}
              </Text>
              <View
                style={{
                  width: s.size,
                  height: 14,
                  backgroundColor: theme.accent,
                  borderRadius: 2,
                }}
              />
              <Text
                style={{
                  fontFamily: "DMSans_400Regular",
                  fontSize: 10.5,
                  color: FOOTER_TEXT_FAINT,
                }}
              >
                {s.size}px
              </Text>
            </View>
          ))}
        </View>
      </SpacingColumn>

      <SpacingColumn title="Border radii" accent={theme.accent}>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 14 }}>
          {RADII_SCALE.map((r) => (
            <View key={r.name} style={{ alignItems: "center", gap: 4, width: 56 }}>
              <View
                style={{
                  width: 44,
                  height: 44,
                  backgroundColor: `${theme.accent}33`,
                  borderWidth: 1,
                  borderColor: theme.accent,
                  borderRadius: r.size,
                }}
              />
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 9.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOOTER_TEXT,
                }}
              >
                {r.name}
              </Text>
            </View>
          ))}
        </View>
      </SpacingColumn>

      <SpacingColumn title="Stroke widths" accent={theme.accent}>
        <View style={{ gap: 12 }}>
          {STROKES_SCALE.map((w) => (
            <View key={w} style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <Text
                style={{
                  width: 38,
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 10.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOOTER_TEXT_DIM,
                }}
              >
                {w}px
              </Text>
              <View style={{ flex: 1, height: w, backgroundColor: theme.accent, maxWidth: 160 }} />
            </View>
          ))}
        </View>
      </SpacingColumn>
    </View>
  );
}

function SpacingColumn({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.25)",
        borderWidth: 1,
        borderColor: FOOTER_BORDER,
        borderRadius: 8,
        padding: 14,
        gap: 12,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View style={{ width: 14, height: 1, backgroundColor: accent }} />
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 10,
            letterSpacing: 2.4,
            textTransform: "uppercase",
            color: accent,
          }}
        >
          {title}
        </Text>
      </View>
      {children}
    </View>
  );
}

/* ─────────────────────── Text (structural specimens) ─────────────────────── */

const STRUCT_EYEBROW = "Eyebrow Label";
const STRUCT_HEADLINE = "Section Headline";
const STRUCT_HEADLINE_ACCENT = "Headline";
const STRUCT_SUBHEAD = "Subhead description sits on this line.";
const STRUCT_BODY = "Body paragraph copy sits in this block.";
const STRUCT_BULLETS = ["Bullet item one", "Bullet item two", "Bullet item three"];
const STRUCT_CTA = "Primary Action";
const STRUCT_SECONDARY_CTA = "Secondary Action";

function TextTab({ theme, wide }: { theme: PaneTheme; wide: boolean }) {
  const cellWidth = wide ? "33.333%" : "50%";
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        marginHorizontal: -7,
        marginVertical: -7,
      }}
    >
      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="Eyebrow">
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 16, height: 1, backgroundColor: theme.accent }} />
          <Text
            style={{
              fontFamily: theme.bodySemiBoldFont,
              fontSize: 11,
              letterSpacing: 3.2,
              textTransform: "uppercase",
              color: theme.accent,
            }}
            numberOfLines={1}
          >
            {STRUCT_EYEBROW}
          </Text>
        </View>
      </VoiceCell>

      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="Headline">
        <Text
          numberOfLines={2}
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 26,
            lineHeight: 30,
            color: FOOTER_TEXT,
            letterSpacing: -0.3,
          }}
        >
          {renderHeadline(STRUCT_HEADLINE, STRUCT_HEADLINE_ACCENT, theme)}
        </Text>
      </VoiceCell>

      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="Subhead">
        <Text
          numberOfLines={2}
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 13,
            lineHeight: 19,
            color: FOOTER_TEXT_DIM,
          }}
        >
          {STRUCT_SUBHEAD}
        </Text>
      </VoiceCell>

      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="Body">
        <Text
          numberOfLines={3}
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12.5,
            lineHeight: 19,
            color: FOOTER_TEXT_DIM,
          }}
        >
          {STRUCT_BODY}
        </Text>
      </VoiceCell>

      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="Bullet">
        <View style={{ gap: 5 }}>
          {STRUCT_BULLETS.map((b) => (
            <View key={b} style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
              <View
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  backgroundColor: theme.accent,
                }}
              />
              <Text
                style={{
                  fontFamily: theme.bodyMediumFont,
                  fontSize: 12,
                  lineHeight: 17,
                  color: FOOTER_TEXT,
                  flex: 1,
                }}
                numberOfLines={1}
              >
                {b}
              </Text>
            </View>
          ))}
        </View>
      </VoiceCell>

      <VoiceCell width={cellWidth as ViewStyle["width"]} accent={theme.accent} label="CTA">
        <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
          <View
            style={{
              backgroundColor: theme.accent,
              borderRadius: 6,
              paddingHorizontal: 12,
              paddingVertical: 8,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize: 10.5,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: theme.accentText,
              }}
              numberOfLines={1}
            >
              {STRUCT_CTA}
            </Text>
          </View>
          <View
            style={{
              borderRadius: 6,
              borderWidth: 1,
              borderColor: theme.accent,
              paddingHorizontal: 12,
              paddingVertical: 8,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize: 10.5,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: theme.accent,
              }}
              numberOfLines={1}
            >
              {STRUCT_SECONDARY_CTA}
            </Text>
          </View>
        </View>
      </VoiceCell>
    </View>
  );
}

function VoiceCell({
  width,
  accent,
  label,
  children,
}: {
  width: ViewStyle["width"];
  accent: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View style={{ width, padding: 7 }}>
      <View
        style={{
          backgroundColor: "rgba(0,0,0,0.25)",
          borderWidth: 1,
          borderColor: FOOTER_BORDER,
          borderLeftWidth: 3,
          borderLeftColor: accent,
          borderRadius: 8,
          paddingVertical: 12,
          paddingHorizontal: 14,
          gap: 8,
          minHeight: 122,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 9.5,
            letterSpacing: 2.4,
            textTransform: "uppercase",
            color: FOOTER_TEXT_FAINT,
          }}
        >
          {label}
        </Text>
        {children}
      </View>
    </View>
  );
}

/* ─────────────────────── Style (design thesis) ─────────────────────── */

function StyleTab({ theme, wide }: { theme: PaneTheme; wide: boolean }) {
  const cellWidth = wide ? "50%" : "100%";
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        marginHorizontal: -6,
        marginVertical: -6,
      }}
    >
      {STYLE_SLOTS.map((slot) => (
        <View key={slot} style={{ width: cellWidth as ViewStyle["width"], padding: 6 }}>
          <View
            style={{
              backgroundColor: "rgba(0,0,0,0.25)",
              borderWidth: 1,
              borderColor: FOOTER_BORDER,
              borderLeftWidth: 3,
              borderLeftColor: theme.accent,
              borderRadius: 8,
              paddingVertical: 14,
              paddingHorizontal: 16,
              gap: 8,
              minHeight: 130,
            }}
          >
            <Text
              style={{
                fontFamily: "DMSans_600SemiBold",
                fontSize: 10.5,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: theme.accent,
              }}
            >
              {slot}
            </Text>
            <Text
              style={{
                fontFamily: theme.displayMediumFont,
                fontSize: 17,
                lineHeight: 23,
                color: FOOTER_TEXT,
              }}
            >
              {theme.atoms.style[slot]}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function renderHeadline(headline: string, accent: string | undefined, theme: PaneTheme) {
  if (!accent || !headline.includes(accent)) return headline;
  const parts = headline.split(accent);
  return parts.map((seg, i, arr) => (
    <Text key={i}>
      {seg}
      {i < arr.length - 1 ? (
        <Text
          style={{
            fontFamily: theme.displayItalicFont,
            color: theme.accent,
          }}
        >
          {accent}
        </Text>
      ) : null}
    </Text>
  ));
}
